/**
 * A base de posicionamento dos painéis que flutuam sobre um campo — G1 do pós-V1.
 *
 * ## O defeito que este arquivo existe para corrigir
 *
 * Calendário, lista do seletor, lista da multisseleção e o painel de menções eram todos
 * `absolute … mt-2`: abriam SEMPRE para baixo e SEMPRE dentro do contêiner do campo. Num
 * modal — cujo corpo é `max-h-[85vh] overflow-y-auto` — isso recorta o painel na borda
 * inferior, e o campo de data do último bloco do formulário ficava sem os dias.
 *
 * `z-index` não resolve recorte: ele decide quem pinta por cima, não quem escapa do
 * `overflow`. Quem escapa é o portal — e daí vêm as duas regras deste arquivo.
 *
 * ## Regra 1 — o destino do portal é o diálogo, nunca o `document.body` cego
 *
 * `Sobreposicao` marca como `inert` todo irmão do diálogo em `document.body`
 * (`aplicarIsolamento`). Um painel portado para o `body` com um modal aberto nasceria
 * INERTE: visível, e surdo a clique e a teclado. Por isso o destino é
 * `useDestinoDosAvisos()` — o topo da pilha de sobreposições, que é o mesmo caminho que
 * a pilha de toasts já usa. Sem diálogo aberto, o `body` é o destino certo.
 *
 * O nó da pilha é a `<div role="dialog">` de `Sobreposicao`, que é `fixed inset-0` e
 * **não tem `transform`**. Isso importa: `position: fixed` dentro de um ancestral com
 * `transform` passa a se medir por ele, e as coordenadas de viewport deste arquivo
 * ficariam erradas. O `-translate-x-1/2` do `Modal` está num filho, não nesse nó.
 *
 * ## Regra 2 — quem mede é a âncora, e ela nem sempre é um elemento
 *
 * O calendário e os seletores se ancoram na CAIXA do campo; a menção se ancora no
 * **cursor de texto**, que é um `Range` e não um nó. Por isso a função recebe um medidor
 * (`() => DOMRect | null`) em vez de uma ref: os dois casos cabem, e nenhum deles precisa
 * saber do outro.
 *
 * ## O que este arquivo NÃO faz
 *
 * Não guarda valor, não decide conteúdo e não fecha nada. Cada consumidor mantém as suas
 * regras — `min`/`max` do calendário, a lista aberta do seletor, o teclado das menções.
 * O que se compartilha é a geometria, que era onde os quatro erravam igual.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useDestinoDosAvisos } from './sobreposicao.tsx'

/** Respiro entre a borda do campo e o painel. */
const FOLGA = 6
/** Margem mínima até a borda da janela — painel colado no limite não se lê. */
const MARGEM = 8
/**
 * Quanto o lado de cima precisa ser MELHOR para valer a virada.
 *
 * Sem uma folga, um campo quase no meio da tela faria o painel pular de lado a cada
 * pixel de rolagem, porque os dois espaços se alternam por um fio.
 */
const GANHO_MINIMO_PARA_VIRAR = 24

export interface GeometriaDoPainel {
  readonly left: number
  readonly top: number
  /** Borda inferior ancorada quando abre acima; a altura real fica a cargo do layout. */
  readonly bottom: number | null
  readonly width: number
  readonly maxHeight: number
  /** O painel virou para cima? Só a animação e os testes precisam saber. */
  readonly acima: boolean
}

export interface OpcoesDoPainel {
  /** Altura que o painel gostaria de ter. A geometria devolve no máximo isto. */
  readonly alturaDesejada: number
  /**
   * Largura do painel. `'campo'` copia a largura da âncora — é o que o seletor quer,
   * para a lista ter a medida do campo. Um número fixa a largura (o calendário usa 256,
   * que é o `w-64` do protótipo).
   */
  readonly largura: number | 'campo'
  /** A que borda da âncora o painel se alinha quando sobra espaço. */
  readonly alinhar?: 'inicio' | 'fim'
}

/**
 * Onde o painel cabe, em coordenadas de viewport, recalculado enquanto ele estiver
 * aberto.
 *
 * `scroll` é ouvido na fase de CAPTURA e com `passive`: o corpo do modal rola por dentro,
 * e um ouvinte só no `window` não veria essa rolagem — o painel ficaria para trás,
 * flutuando sobre um campo que já saiu de baixo dele.
 */
export function usePosicaoDoPainel(
  aberto: boolean,
  medirAncora: () => DOMRect | null,
  opcoes: OpcoesDoPainel,
): GeometriaDoPainel | null {
  const [geometria, setGeometria] = useState<GeometriaDoPainel | null>(null)
  // O medidor e as opções entram por ref para não reinstalar os ouvintes a cada render:
  // quem chama monta a função no corpo do componente, e ela é nova toda vez.
  const medidor = useRef(medirAncora)
  medidor.current = medirAncora
  const config = useRef(opcoes)
  config.current = opcoes

  const recalcular = useCallback(() => {
    const caixa = medidor.current()
    if (caixa === null) {
      setGeometria(null)
      return
    }
    const { alturaDesejada, largura, alinhar = 'inicio' } = config.current
    const alturaDaJanela = window.innerHeight
    const larguraDaJanela = window.innerWidth

    const espacoAbaixo = alturaDaJanela - MARGEM - caixa.bottom - FOLGA
    const espacoAcima = caixa.top - MARGEM - FOLGA
    /*
      Vira para cima quando o painel NÃO CABE INTEIRO embaixo e em cima sobra mais.

      A primeira versão comparava com um piso fixo de 140px — "abaixo disto o espaço não
      serve nem para o cabeçalho e uma linha". O piso é bom para uma lista e mentiroso
      para o calendário: a captura do PÓS-V1-019 mostrou o painel ABRINDO PARA BAIXO com
      150px, porque 150 > 140, e 150px de um calendário de 330 são a barra do mês e duas
      semanas. Cortado — que é exatamente o que o relato descreve.

      O critério certo não é um número absoluto: é se o painel cabe. Se não cabe embaixo
      e em cima sobra mais, vira. A folga evita o pula-pula no empate, e a segunda
      condição continua impedindo a virada para um lado de cima ainda pior num campo
      espremido entre as duas bordas.
    */
    const cabeAbaixo = espacoAbaixo >= alturaDesejada
    const acima = !cabeAbaixo && espacoAcima > espacoAbaixo + GANHO_MINIMO_PARA_VIRAR
    const disponivel = acima ? espacoAcima : espacoAbaixo
    const maxHeight = Math.max(0, Math.min(alturaDesejada, disponivel))

    const larguraFinal = Math.min(
      largura === 'campo' ? caixa.width : largura,
      larguraDaJanela - MARGEM * 2,
    )
    const bruto = alinhar === 'fim' ? caixa.right - larguraFinal : caixa.left
    const left = Math.max(MARGEM, Math.min(bruto, larguraDaJanela - larguraFinal - MARGEM))
    const top = acima ? caixa.top - FOLGA - maxHeight : caixa.bottom + FOLGA
    // maxHeight é um teto, não a altura do conteúdo. Ancorar a borda inferior mantém
    // os mesmos 6px em uma lista de uma linha ou de 240px, inclusive após carga assíncrona.
    const bottom = acima ? alturaDaJanela - caixa.top + FOLGA : null

    setGeometria((anterior) =>
      anterior !== null
      && anterior.left === left
      && anterior.top === top
      && anterior.bottom === bottom
      && anterior.width === larguraFinal
      && anterior.maxHeight === maxHeight
      && anterior.acima === acima
        ? anterior
        : { left, top, bottom, width: larguraFinal, maxHeight, acima },
    )
  }, [])

  /*
    `useLayoutEffect` para a primeira medida: com `useEffect` o painel chega a pintar uma
    vez na posição antiga, e o salto aparece.
  */
  useLayoutEffect(() => {
    if (!aberto) {
      setGeometria(null)
      return undefined
    }
    recalcular()
    return undefined
  }, [aberto, recalcular])

  useEffect(() => {
    if (!aberto) return undefined
    const aoMover = (): void => { recalcular() }
    window.addEventListener('resize', aoMover)
    window.addEventListener('scroll', aoMover, { capture: true, passive: true })
    return () => {
      window.removeEventListener('resize', aoMover)
      window.removeEventListener('scroll', aoMover, { capture: true })
    }
  }, [aberto, recalcular])

  return geometria
}

/**
 * O painel em si: portado para o destino certo e posicionado por `fixed`.
 *
 * `data-painel-flutuante` não é enfeite — é como o clique-fora de quem chama reconhece
 * "isto ainda é o meu painel" depois que o portal o tirou de dentro da caixa do campo.
 * Sem essa marca, o primeiro `mousedown` dentro do calendário seria lido como clique
 * fora, e o painel fecharia antes de o dia receber o clique.
 */
export function PainelFlutuante({
  geometria,
  className,
  children,
  /**
   * A tag do painel. `ul` existe porque a lista do seletor tem filhos `<li>`, e `<li>`
   * solto dentro de `<div>` é marcação inválida — o portal não é desculpa para isso.
   */
  as: Tag = 'div',
  ...resto
}: {
  geometria: GeometriaDoPainel | null
  className: string
  children: ReactNode
  as?: 'div' | 'ul'
} & Omit<React.HTMLAttributes<HTMLElement>, 'className' | 'children' | 'style'>): React.JSX.Element | null {
  const destino = useDestinoDosAvisos()
  if (geometria === null) return null
  return createPortal(
    <Tag
      {...resto}
      data-painel-flutuante=""
      data-acima={geometria.acima ? '' : undefined}
      className={className}
      style={{
        position: 'fixed',
        left: geometria.left,
        top: geometria.acima ? undefined : geometria.top,
        bottom: geometria.bottom ?? undefined,
        width: geometria.width,
        maxHeight: geometria.maxHeight,
      }}
    >
      {children}
    </Tag>,
    destino ?? document.body,
  )
}

/**
 * O clique foi FORA do conjunto campo + painel?
 *
 * Existe porque o portal separou os dois nós: `caixa.contains(alvo)` deixou de ser a
 * pergunta certa no dia em que o painel saiu de dentro da caixa.
 */
export function foraDoConjunto(alvo: EventTarget | null, caixa: HTMLElement | null): boolean {
  if (!(alvo instanceof Node)) return true
  if (caixa?.contains(alvo) === true) return false
  const elemento = alvo instanceof Element ? alvo : alvo.parentElement
  return (elemento?.closest('[data-painel-flutuante]') ?? null) === null
}
